import React, { useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Draggable from 'react-draggable';
import { FaRegClock } from 'react-icons/fa';
import { FiPause, FiPlay, FiX, FiRotateCcw, FiClock } from 'react-icons/fi';
import { MdStop } from 'react-icons/md';
import { BiLogIn, BiLogOut } from 'react-icons/bi';
import Select from 'react-select';
import { timerActionTypes } from '@store/Timer/Timer.actionTypes';
import styles from './TimeTrackingPopup.module.css';

const TimeTrackingPopup = React.memo(
  ({
    open,
    onClose,
    onPlayPause,
    onCancel,
    // COMMENTED OUT: onPunchInOut,
    isPlaying,
    time = '0:00:00',
    todayTime = '7:20:57',
    projects = [], // Keep for backwards compatibility
    companyId,
  }) => {
    const dispatch = useDispatch();

    // Utility function to truncate text with ellipsis
    const truncateText = (text, maxLength) => {
      if (!text || text.length <= maxLength) {
        return text;
      }
      return text.substring(0, maxLength) + '...';
    };

    // Get user and team data for fetching projects/tasks
    const user = useSelector((state) => ({
      userProfile: state.auth?.profile,
      activeCompany: state.auth?.profile?.activeCompany,
    }));

    // Get timer state for projects, tasks, and session
    const timerState = useSelector((state) => ({
      projects: state.timer.projects,
      tasks: state.timer.tasks,
      selectedProject: state.timer.selectedProject,
      projectsLoading: state.timer.projectsLoading,
      tasksLoading: state.timer.tasksLoading,
      projectsError: state.timer.projectsError,
      tasksError: state.timer.tasksError,
      timerSession: state.timer.timerSession,
      loading: state.timer.loading, // Add loading state for button disabling
      // COMMENTED OUT: Punch state from Redux
      // punchStatus: state.timer.punchStatus
    }));

    // COMMENTED OUT: NEW: Separate logic for timer controls vs punch button
    // Timer controls: enabled if active session exists OR daily punched in
    // const isTimerControlsDisabled = !timerState.punchStatus?.isPunchedIn;

    // COMMENTED OUT: Punch button: shows daily status only (independent of active session)
    // const dailyPunchStatus = timerState.punchStatus?.dailyPunchStatus;
    // const isDailyPunchedIn = dailyPunchStatus?.isPunchedIn || false;
    // const hasActiveSession = timerState.punchStatus?.activeSession?.exists || false;

    // COMMENTED OUT: Separate disabled logic for punch in vs punch out
    // const isPunchInDisabled = false; // Punch in is never disabled (except loading)
    // const isPunchOutDisabled = hasActiveSession && isPlaying; // Punch out disabled only when timer running

    // COMMENTED OUT: Overall button disabled state
    // const isPunchButtonDisabled = isDailyPunchedIn ? isPunchOutDisabled : isPunchInDisabled;

    // Fetch projects when popup opens
    useEffect(() => {
      if (open && user?.activeCompany?.companyId) {
        dispatch({
          type: timerActionTypes.FETCH_PROJECTS,
          payload: {
            companyId: user?.activeCompany?.companyId,
          },
        });
      }
    }, [open, user?.activeCompany?.companyId, dispatch]);

    // Fetch tasks when project is selected
    useEffect(() => {
      if (
        timerState.timerSession.projectId &&
        user.userProfile?.id &&
        user?.activeCompany?.companyId
      ) {
        dispatch({
          type: timerActionTypes.FETCH_TASKS,
          payload: {
            projectId: timerState.timerSession.projectId,
            userId: user.userProfile.id,
            companyId: user?.activeCompany?.companyId,
          },
        });
      } else {
        // Clear tasks when no project is selected
        dispatch({
          type: timerActionTypes.CLEAR_TASKS,
        });
      }
      console.log('WebTimeTracking : Tasks', timerState.tasks);
    }, [
      timerState.timerSession.projectId,
      user.userProfile?.id,
      user?.activeCompany?.companyId,
      dispatch,
    ]);

    // Transform projects data to Select options format
    const projectOptions = useMemo(() => {
      if (
        timerState.projects &&
        Array.isArray(timerState.projects) &&
        timerState.projects.length > 0
      ) {
        // Use Redux projects data
        return timerState.projects.map((project) => ({
          value: project.id,
          label: project.name || 'Untitled Project',
        }));
      } else if (projects && Array.isArray(projects) && projects.length > 0) {
        // Fallback to prop projects for backwards compatibility
        return projects.map((project) => ({
          value: project.id,
          label:
            project.data?.project_name ||
            project.project_name ||
            'Untitled Project',
        }));
      }

      // Return empty array when no projects are available
      return [];
    }, [timerState.projects, projects]);

    // Find current selected project option
    const selectedProject = useMemo(() => {
      // First try to find by timerSession.projectId
      if (timerState.timerSession.projectId) {
        const found = projectOptions.find(
          (option) => option.value === timerState.timerSession.projectId
        );
        if (found) return found;
      }

      // Fallback to Redux selectedProject if available
      if (timerState.selectedProject && timerState.selectedProject.value) {
        const found = projectOptions.find(
          (option) => option.value === timerState.selectedProject.value
        );
        if (found) return found;
      }

      return null;
    }, [
      projectOptions,
      timerState.timerSession.projectId,
      timerState.selectedProject,
    ]);

    // Handle project selection change
    const handleProjectChange = (selectedOption) => {
      // Update selected project in Redux for consistency
      dispatch({
        type: timerActionTypes.SET_SELECTED_PROJECT,
        payload: { project: selectedOption },
      });

      // Update timer session directly through Redux
      dispatch({
        type: timerActionTypes.SET_TIMER_SESSION,
        payload: {
          projectId: selectedOption ? selectedOption.value : '',
          projectName: selectedOption ? selectedOption.label : '',
          taskName: '', // Clear task when project changes
          taskId: '', // Clear task ID when project changes
          companyId: companyId || timerState.timerSession.companyId,
        },
      });
    };

    // Handle task selection change
    const handleTaskNameChange = (selectedOption) => {
      // Update timer session directly through Redux
      dispatch({
        type: timerActionTypes.SET_TIMER_SESSION,
        payload: {
          taskName: selectedOption
            ? selectedOption.task?.Title || selectedOption.label
            : '',
          taskId: selectedOption ? selectedOption.value : '',
        },
      });
    };

    // Handle task details change
    const handleTaskDetailsChange = (e) => {
      const value = e.target.value; // Extract value immediately to avoid event pooling issues
      // Update timer session directly through Redux
      dispatch({
        type: timerActionTypes.SET_TIMER_SESSION,
        payload: {
          taskDetails: value,
        },
      });
    };

    // Transform tasks data to Select options format
    const taskOptions = useMemo(() => {
      if (
        timerState.tasks &&
        Array.isArray(timerState.tasks) &&
        timerState.tasks.length > 0
      ) {
        // Use dynamic tasks from Redux with format "Task Title (STATUS)"
        return timerState.tasks.map((task) => ({
          value: task.id,
          label: `${task.Title} (${task.Status})`,
          task: task,
        }));
      }

      // Return empty array when no tasks are available
      return [];
    }, [timerState.tasks]);

    const selectedTaskName = useMemo(() => {
      // First try to find by taskId
      if (timerState.timerSession.taskId) {
        const found = taskOptions.find(
          (option) => option.value === timerState.timerSession.taskId
        );
        if (found) return found;
      }

      // Fallback to find by taskName (for backwards compatibility)
      if (timerState.timerSession.taskName) {
        const found = taskOptions.find(
          (option) => option.value === timerState.timerSession.taskName
        );
        if (found) return found;
      }

      return null;
    }, [
      taskOptions,
      timerState.timerSession.taskId,
      timerState.timerSession.taskName,
    ]);
    if (!open) return null;
    return (
      <Draggable bounds="body" disabled={timerState.loading}>
        <div className={styles.popupContainer}>
          {timerState.loading && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingMessage}>
                <div className={styles.loadingSpinner}></div>
                <span>Processing timer...</span>
              </div>
            </div>
          )}
          <button
            onClick={onClose}
            className={`${styles.closeButton} ${
              timerState.loading ? styles.disabled : ''
            }`}
            title={timerState.loading ? 'Processing...' : 'Close'}
            disabled={timerState.loading}
          >
            <FiX />
          </button>
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              {/* Play/Stop button code commented out because now we have added button at the bottom of the popup*/}
              {/* <div className={styles.buttonGroup}>
              <button
                onClick={onPlayPause}
                className={`${styles.iconCircle} ${timerState.loading ? styles.disabled : ''}`}
                aria-label={isPlaying ? 'Stop timer' : 'Start timer'}
                tabIndex={0}
                disabled={timerState.loading}
                title={timerState.loading ? 'Processing...' : (isPlaying ? 'Stop timer' : 'Start timer')}
              >
                {timerState.loading ? (
                  <div className={styles.loadingSpinner}></div>
                ) : isPlaying ? (
                  <MdStop className={styles.stopIcon} />
                ) : (
                  <FiPlay className={styles.playIcon} />
                )}
              </button>
              <button
                onClick={onCancel}
                className={`${styles.iconCircle} ${styles.cancelButton} ${timerState.loading ? styles.disabled : ''}`}
                aria-label="Cancel timer"
                tabIndex={0}
                disabled={timerState.loading || (!isPlaying && time === '0:00:00')}
                title={timerState.loading ? 'Processing...' : 'Cancel timer'}
              >
                {timerState.loading ? (
                  <div className={styles.loadingSpinner}></div>
                ) : (
                  <FiRotateCcw className={styles.cancelIcon} />
                )}
              </button>
            </div> */}
              <div className={styles.headerInfo}>
                <span className={styles.projectName}>
                  {truncateText(
                    selectedProject ? selectedProject.label : 'Select Project',
                    25
                  )}
                </span>
                <span className={styles.taskName}>
                  {truncateText(
                    selectedTaskName
                      ? selectedTaskName.task
                        ? selectedTaskName.task.Title
                        : selectedTaskName.label
                      : 'Select Task',
                    20
                  )}
                </span>
                {/* <span className={styles.noLimit}>No limit</span> */}
              </div>
            </div>
            <div className={styles.headerRight}>
              <span className={styles.timer}>{time}</span>
              {/* <span className={styles.today}>Today: {todayTime}</span> */}
            </div>
          </div>
          <div className={styles.divider} />
          <div className={styles.content}>
            {/* COMMENTED OUT: Punch In/Out Section */}
            {/* 
          <div className={styles.punchContainer}>
            <button
              onClick={onPunchInOut}
              className={`${styles.punchButton} ${
                isDailyPunchedIn 
                  ? styles.punchButtonPunchedIn 
                  : styles.punchButtonPunchIn
              } ${
                timerState.punchStatus?.loading || isPunchButtonDisabled ? styles.disabled : ''
              }`}
              disabled={timerState.punchStatus?.loading || isPunchButtonDisabled}
              title={
                timerState.punchStatus?.loading ? 'Processing...' :
                isDailyPunchedIn && isPunchOutDisabled ? 'Stop timer before punching out' :
                isDailyPunchedIn ? 'Punch Out for today' : 'Punch In for today'
              }
            >
              {timerState.punchStatus?.loading ? (
                <>
                  <div className={styles.punchButtonLoadingSpinner}></div>
                  <span className={styles.punchButtonText}>Processing...</span>
                </>
              ) : (
                <>
                  {isDailyPunchedIn ? (
                    <BiLogOut className={styles.punchButtonIcon} />
                  ) : (
                    <BiLogIn className={styles.punchButtonIcon} />
                  )}
                  <span className={styles.punchButtonText}>
                    {isDailyPunchedIn ? 'Punch Out' : 'Punch In'}
                  </span>
                </>
              )}
            </button>
            
            <div className={`${styles.punchStatus} ${
              (!isTimerControlsDisabled) 
                ? styles.punchStatusPunchedIn 
                : styles.punchStatusPunchedOut
            }`}>
              {(() => {
                if (hasActiveSession && !isDailyPunchedIn) {
                  return '🔄 Complete current session or punch in for new day';
                } else if (hasActiveSession && isDailyPunchedIn) {
                  return '✓ Timer session active - ready to track time';
                } else if (!hasActiveSession && isDailyPunchedIn) {
                  return '✓ Punched in - ready to start tracking time';
                } else {
                  return '⏸ Punch in to start tracking time';
                }
              })()}
            </div>
          </div>
          */}

            {/* Timer Controls Section */}
            {/* COMMENTED OUT: Timer controls disabled wrapper */}
            {/* <div className={isTimerControlsDisabled ? styles.timerControlsDisabled : ''}> */}
            <div>
              <label className={styles.label}>PROJECT *</label>
              <Select
                options={projectOptions}
                value={selectedProject}
                onChange={handleProjectChange}
                placeholder={
                  // COMMENTED OUT: isTimerControlsDisabled ? "Punch in first to select project" :
                  isPlaying
                    ? 'Stop timer to change project'
                    : timerState.projectsLoading
                      ? 'Loading projects...'
                      : projectOptions.length === 0
                        ? 'No projects available'
                        : 'Select a project...'
                }
                // COMMENTED OUT: isDisabled={isTimerControlsDisabled || isPlaying || timerState.projectsLoading}
                isDisabled={isPlaying || timerState.projectsLoading}
                isLoading={timerState.projectsLoading}
                classNamePrefix="react-select"
                menuPortalTarget={
                  typeof window !== 'undefined' ? document.body : null
                }
                menuPosition="fixed"
                styles={{
                  container: (base) => ({ ...base, marginBottom: 12 }),
                  control: (base) => ({
                    ...base,
                    borderRadius: 6,
                    minHeight: 36,
                    borderColor: !selectedProject
                      ? '#dc3545'
                      : base.borderColor,
                    // COMMENTED OUT: opacity: (isTimerControlsDisabled || isPlaying || timerState.projectsLoading) ? 0.6 : 1,
                    // COMMENTED OUT: cursor: (isTimerControlsDisabled || isPlaying || timerState.projectsLoading) ? 'not-allowed' : 'pointer'
                    opacity: isPlaying || timerState.projectsLoading ? 0.6 : 1,
                    cursor:
                      isPlaying || timerState.projectsLoading
                        ? 'not-allowed'
                        : 'pointer',
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
              <label className={styles.label}>TASK NAME *</label>
              <Select
                options={taskOptions}
                value={selectedTaskName}
                onChange={handleTaskNameChange}
                placeholder={
                  // COMMENTED OUT: isTimerControlsDisabled ? "Punch in first to select task" :
                  isPlaying
                    ? 'Stop timer to change task'
                    : timerState.tasksLoading
                      ? 'Loading tasks...'
                      : !timerState.timerSession.projectId
                        ? 'Select a project first'
                        : taskOptions.length === 0
                          ? 'No tasks available for this project'
                          : 'Select task name...'
                }
                // COMMENTED OUT: isDisabled={isTimerControlsDisabled || isPlaying || !timerState.timerSession.projectId || timerState.tasksLoading}
                isDisabled={
                  isPlaying ||
                  !timerState.timerSession.projectId ||
                  timerState.tasksLoading
                }
                isLoading={timerState.tasksLoading}
                classNamePrefix="react-select"
                menuPortalTarget={
                  typeof window !== 'undefined' ? document.body : null
                }
                menuPosition="fixed"
                styles={{
                  container: (base) => ({ ...base, marginBottom: 12 }),
                  control: (base) => ({
                    ...base,
                    borderRadius: 6,
                    minHeight: 36,
                    borderColor: !selectedTaskName
                      ? '#dc3545'
                      : base.borderColor,
                    // COMMENTED OUT: opacity: (isTimerControlsDisabled || isPlaying || !timerState.timerSession.projectId || timerState.tasksLoading) ? 0.6 : 1,
                    // COMMENTED OUT: cursor: (isTimerControlsDisabled || isPlaying || !timerState.timerSession.projectId || timerState.tasksLoading) ? 'not-allowed' : 'pointer'
                    opacity:
                      isPlaying ||
                      !timerState.timerSession.projectId ||
                      timerState.tasksLoading
                        ? 0.6
                        : 1,
                    cursor:
                      isPlaying ||
                      !timerState.timerSession.projectId ||
                      timerState.tasksLoading
                        ? 'not-allowed'
                        : 'pointer',
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
              <label className={styles.label}>TASK DETAILS</label>
              <textarea
                value={timerState.timerSession.taskDetails || ''}
                onChange={handleTaskDetailsChange}
                placeholder={
                  // COMMENTED OUT: isTimerControlsDisabled ? "Punch in first to add task details" :
                  isPlaying
                    ? 'Stop timer to edit task details'
                    : 'Enter task details (optional)...'
                }
                className={styles.textarea}
                rows={3}
                // COMMENTED OUT: disabled={isTimerControlsDisabled || isPlaying}
                disabled={isPlaying}
                style={{
                  // COMMENTED OUT: opacity: (isTimerControlsDisabled || isPlaying) ? 0.6 : 1,
                  // COMMENTED OUT: cursor: (isTimerControlsDisabled || isPlaying) ? 'not-allowed' : 'text'
                  opacity: isPlaying ? 0.6 : 1,
                  cursor: isPlaying ? 'not-allowed' : 'text',
                }}
              />

              {/* Action Buttons */}
              <div className={styles.actionButtons}>
                <button
                  onClick={onPlayPause}
                  className={`${styles.primaryButton} ${
                    // COMMENTED OUT: (timerState.loading || isTimerControlsDisabled || (!selectedProject || !selectedTaskName)) ? styles.disabled : ''
                    timerState.loading || !selectedProject || !selectedTaskName
                      ? styles.disabled
                      : ''
                  }`}
                  // COMMENTED OUT: disabled={timerState.loading || isTimerControlsDisabled || (!selectedProject || !selectedTaskName)}
                  disabled={
                    timerState.loading || !selectedProject || !selectedTaskName
                  }
                  title={
                    timerState.loading
                      ? 'Processing...'
                      : // COMMENTED OUT: isTimerControlsDisabled ? 'Please punch in first' :
                        !selectedProject
                        ? 'Please select a project'
                        : !selectedTaskName
                          ? 'Please select a task'
                          : isPlaying
                            ? 'Stop timer'
                            : 'Start timer'
                  }
                >
                  {timerState.loading ? (
                    <>
                      <div className={styles.loadingSpinner}></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      {isPlaying ? (
                        <MdStop className={styles.buttonIcon} />
                      ) : (
                        <FiPlay className={styles.buttonIcon} />
                      )}
                      <span>{isPlaying ? 'Stop Timer' : 'Start Timer'}</span>
                    </>
                  )}
                </button>

                <button
                  onClick={onCancel}
                  className={`${styles.secondaryButton} ${
                    // COMMENTED OUT: (timerState.loading || isTimerControlsDisabled || (!isPlaying && time === '0:00:00')) ? styles.disabled : ''
                    timerState.loading || (!isPlaying && time === '0:00:00')
                      ? styles.disabled
                      : ''
                  }`}
                  // COMMENTED OUT: disabled={timerState.loading || isTimerControlsDisabled || (!isPlaying && time === '0:00:00')}
                  disabled={
                    timerState.loading || (!isPlaying && time === '0:00:00')
                  }
                  title={
                    timerState.loading
                      ? 'Processing...'
                      : // COMMENTED OUT: isTimerControlsDisabled ? 'Please punch in first' :
                        !isPlaying && time === '0:00:00'
                        ? 'No active timer to cancel'
                        : 'Cancel timer'
                  }
                >
                  {timerState.loading ? (
                    <>
                      <div className={styles.loadingSpinner}></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <FiRotateCcw className={styles.buttonIcon} />
                      <span>Cancel Timer</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Draggable>
    );
  }
);

// Add display name for better debugging
TimeTrackingPopup.displayName = 'TimeTrackingPopup';

export default TimeTrackingPopup;
